"use client";

import React, { useState } from "react";
import NextLink from "next/link";

import LegalPolicySelector from "@/app/(marketing)/_components/legal-policy-selector";
import { Button } from "@/components/ui/button";
import { useTranslations, getLocale } from "@/lib/i18n-utils";
import { IBM_Plex_Serif } from 'next/font/google';
import LandingNav from "@/app/(marketing)/_components/landing-nav";
import LandingFooter from '@/components/LandingFooter';

const ibmPlexSerif = IBM_Plex_Serif({ subsets: ['latin'], weight: ['300','400','600','700'], display: 'swap' });

const TermsPage = () => {
  const [lang, setLang] = useState<"en" | "da" | "both">("both");

  React.useEffect(() => {
    const stored = getLocale();
    if (stored === "en" || stored === "da") setLang(stored);
    else setLang("both");
  }, []);

  const setLanguage = (l: "en" | "da" | "both") => {
    setLang(l);
    try {
      if (typeof window !== "undefined") {
        if (l === "both") {
          localStorage.removeItem("locale");
          const current = getLocale();
          window.dispatchEvent(new CustomEvent("localeChange", { detail: current }));
        } else {
          localStorage.setItem("locale", l);
          window.dispatchEvent(new CustomEvent("localeChange", { detail: l }));
        }
      }
    } catch (e) {}
  };

  const { locale } = useTranslations();

  const sections = [
    {
      title: locale === "da" ? "1. Accept af vilkår" : "1. Acceptance of Terms",
      body:
        locale === "da"
          ? "Ved at bruge Svendetid accepterer du disse vilkår. Hvis du ikke accepterer dem, må du ikke bruge tjenesten."
          : "By using Svendetid, you agree to these terms. If you do not agree, you must not use the service.",
    },
    {
      title: locale === "da" ? "2. Tjenestens formål" : "2. Scope of the Service",
      body:
        locale === "da"
          ? "Svendetid leverer software til projektstyring, planlægning, tidsregistrering, tilbud, fakturering og relaterede forretningsprocesser for virksomheder."
          : "Svendetid provides software for project management, scheduling, time tracking, quoting, invoicing, and related business operations for companies.",
    },
    {
      title: locale === "da" ? "3. Konto og adgang" : "3. Accounts and Access",
      body:
        locale === "da"
          ? "Du er ansvarlig for at holde dine loginoplysninger sikre og for al aktivitet, der sker under din konto eller organisation."
          : "You are responsible for keeping your login credentials secure and for all activity that occurs under your account or organization.",
    },
    {
      title: locale === "da" ? "4. Betaling og abonnement" : "4. Billing and Subscription",
      body:
        locale === "da"
          ? "Betalte planer faktureres efter den valgte abonnementsmodel. Manglende betaling kan medføre begrænsning eller lukning af adgang til tjenesten."
          : "Paid plans are billed according to the selected subscription model. Failure to pay may result in suspended or terminated access to the service.",
    },
    {
      title: locale === "da" ? "5. Acceptabel brug" : "5. Acceptable Use",
      body:
        locale === "da"
          ? "Du må ikke bruge Svendetid til ulovlige handlinger, misbrug af systemet, forsøg på uautoriseret adgang eller aktiviteter, der kan skade platformen eller andre brugere."
          : "You may not use Svendetid for unlawful conduct, abuse of the system, attempts at unauthorized access, or activities that may harm the platform or other users.",
    },
    {
      title: locale === "da" ? "6. Data og ejerskab" : "6. Data and Ownership",
      body:
        locale === "da"
          ? "Du beholder ejerskabet over de data, du indtaster i Svendetid. Du giver os alene de nødvendige rettigheder til at behandle dataene for at levere og forbedre tjenesten."
          : "You retain ownership of the data you submit to Svendetid. You grant us only the rights necessary to process that data in order to provide and improve the service.",
    },
    {
      title: locale === "da" ? "7. Tilgængelighed og ændringer" : "7. Availability and Changes",
      body:
        locale === "da"
          ? "Vi kan løbende opdatere, ændre eller forbedre funktioner. Vi garanterer ikke uafbrudt drift, men arbejder på at holde tjenesten stabil og tilgængelig."
          : "We may update, change, or improve features over time. We do not guarantee uninterrupted availability, but we work to keep the service stable and accessible.",
    },
    {
      title: locale === "da" ? "8. Ansvarsbegrænsning" : "8. Limitation of Liability",
      body:
        locale === "da"
          ? "Svendetid leveres som udgangspunkt som den er. I det omfang loven tillader det, er vi ikke ansvarlige for indirekte tab, driftstab eller tabt fortjeneste."
          : "Svendetid is provided on an as-is basis. To the extent permitted by law, we are not liable for indirect damages, loss of business, or lost profits.",
    },
    {
      title: locale === "da" ? "9. Opsigelse" : "9. Termination",
      body:
        locale === "da"
          ? "Vi kan suspendere eller afslutte adgangen ved væsentlig misligholdelse af disse vilkår. Du kan til enhver tid stoppe med at bruge tjenesten."
          : "We may suspend or terminate access in the event of a material breach of these terms. You may stop using the service at any time.",
    },
    {
      title: locale === "da" ? "10. Kontakt" : "10. Contact",
      body:
        locale === "da"
          ? "Hvis du har spørgsmål til disse vilkår, kan du kontakte Svendetid via de kontaktoplysninger, der fremgår på platformen."
          : "If you have questions about these terms, you can contact Svendetid using the contact details provided on the platform.",
    },
  ];

  return (
    <div className={`${ibmPlexSerif.className} root ${lang === "en" ? "show-en" : ""} ${lang === "da" ? "show-da" : ""}`}>
      <style>{`
        :root {
          --brand: #304B43;
          --brand-light: #EEF6F2;
          --brand-mid: #C9E1DA;
          --text: #1E293B;
          --muted: #64748B;
          --border: #E2E8F0;
          --bg: #FFFFFF;
          --section-bg: #F8FAFC;
        }
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { font-family: 'Segoe UI', system-ui, -apple-system, sans-serif; }
        .lang-bar { background: var(--brand); color: #fff; display: flex; justify-content: flex-end; align-items: center; gap: 8px; padding: 10px 40px; font-size: 14px; }
        .lang-bar span { opacity: .7; }
        .lang-btn { background: rgba(255,255,255,.15); border: 1px solid rgba(255,255,255,.3); color: #fff; padding: 4px 14px; border-radius: 20px; cursor: pointer; font-size: 13px; transition: background .2s; }
        .lang-btn.active, .lang-btn:hover { background: rgba(255,255,255,.35); }
        header { background: linear-gradient(135deg, var(--brand) 0%, #25443b 100%); color: #fff; padding: 48px 40px 24px; text-align: center; }
        header .logo { font-size: 32px; font-weight: 800; letter-spacing: -1px; margin-bottom: 12px; }
        header .logo span { opacity: .7; }
        header h1 { font-size: 22px; font-weight: 400; opacity: .9; margin-bottom: 8px; }
        header .updated { font-size: 13px; opacity: .65; }
        .wrapper { max-width: 860px; margin: 0 auto; padding: 0 24px 80px; }
        .toc { background: var(--brand-light); border: 1px solid var(--brand-mid); border-radius: 12px; padding: 28px 32px; margin: 8px 0 48px; }
        .toc h2 { font-size: 15px; text-transform: uppercase; letter-spacing: .06em; color: var(--brand); margin-bottom: 14px; }
        .toc ol { padding-left: 20px; }
        .toc li { margin-bottom: 5px; }
        .toc a { color: var(--brand); text-decoration: none; font-size: 15px; }
        .toc a:hover { text-decoration: underline; }
        section { margin-bottom: 52px; scroll-margin-top: 24px; }
        .section-label { display: inline-block; background: var(--brand); color: #fff; font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: .08em; padding: 3px 10px; border-radius: 4px; margin-bottom: 10px; }
        h2 { font-size: 22px; font-weight: 700; color: var(--text); margin-bottom: 6px; line-height: 1.3; }
        h3 { font-size: 17px; font-weight: 600; color: var(--brand); margin: 28px 0 10px; }
        p { margin-bottom: 14px; color: #334155; }
        ul, ol { padding-left: 22px; margin-bottom: 14px; }
        li { margin-bottom: 6px; color: #334155; }
        .card { background: var(--section-bg); border: 1px solid var(--border); border-left: 4px solid var(--brand); border-radius: 8px; padding: 20px 24px; margin-bottom: 20px; }
        .card p:last-child { margin-bottom: 0; }
        .contact-box { background: var(--brand-light); border: 1px solid var(--brand-mid); border-radius: 12px; padding: 28px 32px; text-align: center; }
        .contact-box a { color: var(--brand); font-weight: 600; font-size: 18px; text-decoration: none; }
        .contact-box a:hover { text-decoration: underline; }
        .rights-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-top: 16px; }
        .right-item { background: var(--section-bg); border: 1px solid var(--border); border-radius: 8px; padding: 14px 18px; font-size: 15px; color: #334155; display: flex; align-items: flex-start; gap: 10px; }
        .right-item::before { content: "✓"; color: var(--brand); font-weight: 700; flex-shrink: 0; margin-top: 1px; }
        hr { border: none; border-top: 1px solid var(--border); margin: 60px 0; }
        .en, .da { display: block; }
        .root.show-da .en { display: none; }
        .root.show-en .da { display: none; }
        @media (max-width: 600px) { header { padding: 40px 20px 32px; } .lang-bar { padding: 10px 20px; } .rights-grid { grid-template-columns: 1fr; } .toc { padding: 20px; } h2 { font-size: 19px; } }
      `}</style>

    

      <section className="relative overflow-hidden">
        <LandingNav lang={locale as "da" | "en"} />
        <header className="relative z-10 mx-auto w-full max-w-7xl px-4 text-center">
          <div className="logo mx-auto">Sun<span>dok</span></div>
          <h1 className="en mb-2 w-full max-w-4xl mx-auto font-serif text-xl font-black leading-tight tracking-tight text-slate-900 sm:text-xl md:text-xl lg:text-[2.5rem] lg:leading-[1.05]">{locale === 'da' ? 'Vilkår og betingelser' : 'Terms and Conditions'}</h1>
          <h1 className="da mb-2 w-full max-w-4xl mx-auto font-serif text-xl font-black leading-tight tracking-tight  sm:text-xl md:text-xl lg:text-[2.5rem] lg:leading-[1.05] text-white">{locale === 'da' ? 'Vilkår og betingelser' : 'Terms and Conditions'}</h1>
          <p className="updated text-white">{locale === 'da' ? 'Senest opdateret: 24. marts 2026' : 'Last updated: March 24, 2026'}</p>
        </header>
      </section>

      <div className="wrapper">
        <div className={locale === 'da' ? 'da' : 'en'}>
          <div className="toc">
            <h2>{locale === 'da' ? 'Indhold' : 'Contents'}</h2>
            <ol>
              {sections.map((s) => (
                <li key={String(s.title)}><a href={`#${String(s.title).replace(/[^a-z0-9]/gi, '-')}`}>{s.title}</a></li>
              ))}
            </ol>
          </div>

          <div className="card">
            <div className="grid gap-4">
              {sections.map((section) => (
                <section key={section.title} className="rounded-2xl border border-slate-200 bg-slate-50/80 p-5 sm:p-6">
                  <h2 className="text-lg font-bold text-slate-900 sm:text-xl">{section.title}</h2>
                  <p className="mt-3 text-sm leading-7 text-slate-600 sm:text-base">{section.body}</p>
                </section>
              ))}
            </div>

            <p className="text-sm text-slate-500 mt-6">{locale === 'da' ? 'Senest opdateret: 24. marts 2026' : 'Last updated: March 24, 2026'}</p>
          </div>
        </div>
      </div>
      <LandingFooter lang={locale === "da" ? "da" : "en"} daHref="/terms" enHref="/en/terms" />
    </div>
  );
};

export default TermsPage;
